/**
 * InviteModal - Dialog for creating and sharing 3rd party invite links
 * Enhanced with name/email form and participant tracking
 */

import { useState } from "react";
import { useMutation } from "convex/react";
import { useTranslation } from "react-i18next";
import { api } from "@jourfx/convex/client";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Button,
  Input,
  Label,
  Badge,
  Avatar,
  AvatarFallback,
  ScrollArea,
} from "@jourfx/ui";
import { UserPlus, Copy, Check, Link2, Clock, User, Mail, Info } from "lucide-react";

type Id<T extends string> = string & { __tableName: T };

interface InvitedParticipant {
  id: string;
  name: string;
  email: string;
  status: "pending" | "joined";
  token: string;
}

interface InviteModalProps {
  appointmentId: string;
  onClose: () => void;
}

export function InviteModal({ appointmentId, onClose }: InviteModalProps) {
  const { t } = useTranslation("video");
  // Form state
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");

  // Link state
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Participant tracking
  const [invitedParticipants, setInvitedParticipants] = useState<InvitedParticipant[]>([]);

  const createInviteLink = useMutation(api.video.invite.createInviteLink);

  const handleCreateLink = async () => {
    // Require name before creating link
    if (!inviteName.trim()) {
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      const result = await createInviteLink({
        appointmentId: appointmentId as Id<"appointments">,
        guestName: inviteName.trim(),
        guestEmail: inviteEmail.trim() || undefined,
        sendEmail: !!inviteEmail.trim(),
      });
      setInviteLink(result.inviteUrl);
      setExpiresAt(result.expiresAt);

      // Add to invited participants list
      const newParticipant: InvitedParticipant = {
        id: crypto.randomUUID(),
        name: inviteName.trim(),
        email: inviteEmail.trim(),
        status: "pending",
        token: result.token,
      };
      setInvitedParticipants((prev) => {
        if (prev.some((participant) => participant.token === result.token)) {
          return prev;
        }
        return [...prev, newParticipant];
      });

      // Clear form fields
      setInviteName("");
      setInviteEmail("");
    } catch (err) {
      console.error("Failed to create invite link:", err);
      const message = err instanceof Error ? err.message : t("invite.createFailed");
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!inviteLink) return;

    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleCreateAnother = () => {
    setInviteLink(null);
    setExpiresAt(null);
    setCopied(false);
  };

  const formatExpiry = (timestamp: number) => {
    const diffMs = timestamp - Date.now();
    if (diffMs <= 0) return t("invite.expired");

    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 180) {
      return t("invite.minutes", { count: minutes });
    }

    return new Date(timestamp).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-[420px] sm:max-w-[420px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            {t("invite.title")}
          </SheetTitle>
          <SheetDescription>
            {t("invite.subtitle")}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {errorMessage && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMessage}
            </div>
          )}

          {/* Form section */}
          {!inviteLink && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-name" className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  {t("invite.nameLabel")} <span className="text-destructive">{t("invite.nameRequired")}</span>
                </Label>
                <Input
                  id="invite-name"
                  placeholder={t("invite.namePlaceholder")}
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="invite-email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  {t("invite.emailLabel")} <span className="text-muted-foreground text-xs">{t("invite.emailOptional")}</span>
                </Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder={t("invite.emailPlaceholder")}
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>

              <Button
                onClick={handleCreateLink}
                disabled={loading || !inviteName.trim()}
                className="w-full"
                aria-label={loading ? t("invite.creatingLink") : t("invite.createLink")}
                aria-busy={loading}
              >
                <Link2 className="w-4 h-4 mr-2" />
                {loading ? t("invite.creatingLink") : t("invite.createLink")}
              </Button>
            </div>
          )}

          {/* Generated link section */}
          {inviteLink && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t("invite.linkLabel")}</Label>
                <div className="flex gap-2">
                  <Input value={inviteLink} readOnly className="text-sm font-mono" />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={handleCopy}
                    aria-label={t("invite.copyLink")}
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {copied && (
                  <span className="sr-only" aria-live="polite">{t("invite.linkCopied")}</span>
                )}
              </div>

              {expiresAt && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>{t("invite.validFor", { time: formatExpiry(expiresAt) })}</span>
                </div>
              )}

              <Button
                variant="outline"
                onClick={handleCreateAnother}
                className="w-full"
                aria-label={t("invite.inviteAnother")}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {t("invite.inviteAnother")}
              </Button>
            </div>
          )}

          {/* Invited participants list */}
          {invitedParticipants.length > 0 && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t("invite.invitedPeople")}</Label>
              <ScrollArea className="max-h-[200px]">
                <div className="space-y-2">
                  {invitedParticipants.map((participant) => (
                    <div
                      key={participant.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border"
                    >
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="text-xs bg-primary/10 text-primary">
                          {getInitials(participant.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{participant.name}</p>
                        {participant.email && (
                          <p className="text-xs text-muted-foreground truncate">
                            {participant.email}
                          </p>
                        )}
                      </div>
                      <Badge
                        variant={participant.status === "joined" ? "default" : "secondary"}
                        className="shrink-0"
                      >
                        {participant.status === "joined" ? t("invite.statusJoined") : t("invite.statusPending")}
                      </Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Info box */}
          <div className="bg-muted rounded-lg p-4 text-sm">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="font-medium mb-2">{t("invite.hints.title")}</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>{t("invite.hints.singleUse")}</li>
                  <li>{t("invite.hints.validity")}</li>
                  <li>{t("invite.hints.guestJoins")}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
